import { RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';
import { useIsMobile } from '../hooks/useMediaQuery';
import { usePullToRefresh } from '../hooks/usePullToRefresh';

interface Props {
  onRefresh: () => Promise<void>;
  children: React.ReactNode;
  className?: string;
}

/**
 * Pull-to-Refresh wrapper — only active on mobile.
 * Desktop: renders children as-is with no overhead.
 */
export default function PullToRefresh({ onRefresh, children, className }: Props) {
  const isMobile = useIsMobile();
  const { containerRef, pullDistance, refreshing, handlers } = usePullToRefresh({ onRefresh });

  if (!isMobile) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      {...handlers}
    >
      {/* Pull indicator */}
      <div
        className={cn(
          "absolute left-1/2 -translate-x-1/2 z-50 flex items-center justify-center transition-all duration-200",
          pullDistance > 0 || refreshing ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        style={{ top: Math.max(pullDistance - 40, 4) }}
      >
        <div className={cn(
          "w-9 h-9 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg flex items-center justify-center",
          refreshing && "animate-spin"
        )}>
          <RefreshCw className={cn("w-4 h-4 text-primary transition-transform", !refreshing && "rotate-0")}
            style={{ transform: refreshing ? undefined : `rotate(${Math.min(pullDistance * 3, 360)}deg)` }} />
        </div>
      </div>

      {/* Content with pull offset */}
      <div
        className="transition-transform duration-200 ease-out"
        style={{ transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined }}
      >
        {children}
      </div>
    </div>
  );
}
